/**
 * reactSelectTheme — the app's `react-select` styles, in one place.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 * `react-select` styles through a JS object, not CSS, so it sits outside every gate
 * this programme has: `lint:tokens --check literal` reads stylesheets, and the
 * `inline` check reads `style={{ }}` attributes. A `styles={themeStyles}` prop is
 * neither. Three near-identical copies of the object had therefore drifted quietly:
 *
 *   AIQuestionForm.js            minHeight 40, --input-border, ring 3px @ .15
 *   GenerateAIQuestionsModal.jsx minHeight 40, --input-border, ring 3px @ .15, z 99999
 *   TagSelector.js               minHeight 38, --border-primary, ring 1px, z 9999
 *
 * and all three hardcoded **`#3b82f6`** — the v1 blue — for the focused border, the
 * focus ring and the selected option's fill. So every searchable dropdown in the AI
 * question bank and the coding question bank was blue on a violet-branded app, in
 * both themes, and could never follow an institute's colour. Same defect as the
 * `$fc-accent` indigo in FilterControls; different hiding place.
 *
 * ⚠️ If you add another `react-select`, import this. Do not paste a fourth object —
 * nothing in CI will tell you it has drifted.
 *
 * ── Usage ────────────────────────────────────────────────────────────────────
 *   import { reactSelectTheme } from "../ui";
 *   <Select styles={reactSelectTheme()} … />
 *   <Select styles={reactSelectTheme({ minHeight: 38, ring: 1 })} … />
 *
 * @param {number} minHeight  control height in px (default 40)
 * @param {number} ring       focus-ring width in px (default 3; pass 1 for the
 *                            tighter outline TagSelector used)
 * @param {number} zIndex     menu + portal z-index (default 9999)
 */
export const reactSelectTheme = ({ minHeight = 40, ring = 3, zIndex = 9999 } = {}) => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--input-bg)",
    borderColor: state.isFocused ? "hsl(var(--primary))" : "var(--input-border)",
    borderRadius: "8px",
    minHeight: `${minHeight}px`,
    boxShadow: state.isFocused ? `0 0 0 ${ring}px hsl(var(--primary) / 0.15)` : "none",
    "&:hover": { borderColor: "var(--text-tertiary)" },
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: "var(--card-bg)",
    border: "1px solid var(--border-primary)",
    borderRadius: "8px",
    boxShadow: "0 4px 12px var(--card-shadow)",
    zIndex,
  }),

  // Portaled to <body> by the call sites (`menuPortalTarget`) so the dropdown escapes
  // an ancestor's `overflow: hidden` — AppCard clips it otherwise.
  menuPortal: (base) => ({ ...base, zIndex }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "hsl(var(--primary))"
      : state.isFocused
        ? "var(--bg-hover)"
        : "transparent",
    // The ink on a selected row is the brand's computed foreground, not `#fff`:
    // Phase 7.1 picks it by measured contrast, so a light brand stays readable.
    color: state.isSelected ? "hsl(var(--primary-solid-fg))" : "var(--text-primary)",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: state.isSelected ? "hsl(var(--primary-solid))" : "var(--bg-hover)",
    },
  }),

  // Multi-select chips. Harmless on a single select, so they are always present
  // rather than being a fourth option nobody remembers to pass.
  multiValue: (base) => ({
    ...base,
    backgroundColor: "var(--bg-tertiary)",
    borderRadius: "6px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--text-primary)",
    fontSize: "0.82rem",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--text-secondary)",
    borderRadius: "0 6px 6px 0",
    "&:hover": {
      backgroundColor: "hsl(var(--danger) / 0.12)",
      color: "hsl(var(--danger-fg))",
    },
  }),

  singleValue: (base) => ({ ...base, color: "var(--text-primary)" }),
  input: (base) => ({ ...base, color: "var(--text-primary)" }),
  placeholder: (base) => ({ ...base, color: "var(--text-tertiary)" }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "var(--border-primary)" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--text-tertiary)",
    "&:hover": { color: "var(--text-primary)" },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--text-tertiary)",
    "&:hover": { color: "var(--text-primary)" },
  }),
});

export default reactSelectTheme;
